import { create } from "zustand";

import type { BarcodeLookupResult } from "@/hooks/api/inventory/useBarcodeLookup";

interface ScanChoiceRequest {
  code: string;
  matches: BarcodeLookupResult[];
  resolve: (choice: BarcodeLookupResult | null) => void;
}

interface ScanChoiceState {
  request: ScanChoiceRequest | null;
  /** Ask the user which of `matches` they meant; null if they dismiss. */
  choose: (
    code: string,
    matches: BarcodeLookupResult[],
  ) => Promise<BarcodeLookupResult | null>;
  settle: (choice: BarcodeLookupResult | null) => void;
}

/**
 * INV-22, Decision D8: a scanned code that matches more than one record is
 * never resolved silently. `useBarcodeLookup` awaits `choose()`, and
 * ScanMatchChooserDialog (mounted once in __root.tsx) renders the request --
 * so every scanning screen gets the chooser without wiring anything itself.
 */
export const useScanChoiceStore = create<ScanChoiceState>((set, get) => ({
  request: null,
  choose: (code, matches) =>
    new Promise((resolve) => {
      // A second ambiguous scan while the chooser is open supersedes the
      // first: that scan resolves as "no choice" rather than hanging.
      get().request?.resolve(null);
      set({ request: { code, matches, resolve } });
    }),
  settle: (choice) => {
    get().request?.resolve(choice);
    set({ request: null });
  },
}));
